import {
  getPrescriptionById,
  markPrescriptionDispensed,
  markPrescriptionPharmacistValidated,
  markPrescriptionTransferredToBilling,
  patchPrescriptionLineFulfillment,
} from "@/core/clinical/clinical-store";
import type { PrescriptionLine } from "@/core/clinical/types";
import {
  PRESCRIPTION_LINE_DISPENSE_LABELS,
  PRESCRIPTION_LINE_STOCK_LABELS,
} from "@/core/clinical/types";
import {
  createBillingDocumentFromPrescriptionLines,
  getBillingDocument,
} from "@/core/billing/billing-documents/billing-documents-store";
import type { BillingDocument } from "@/core/billing/billing-documents/types";
import { getCoverageCaseForPrescription } from "@/core/insurance/coverage-case-flow";
import { prescriptionLineRequiresInsurerValidation } from "@/core/insurance/coverage-case-flow";
import { pharmacyInventoryApi } from "@/core/inventory/inventory-api";
import {
  issuePrescriptionLinesAtomically,
  releasePrescriptionLine,
  reservePrescriptionLine,
} from "@/core/inventory/pharmacy-stock-sync";
import { getPatientById } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { pushLocalNotification } from "@/core/notifications/local-notifications";

export interface LineActionResult {
  ok: boolean;
  reason?: string;
}

export function canTransferLineToBilling(
  prescriptionId: string,
  lineId: string,
): LineActionResult {
  const prescription = getPrescriptionById(prescriptionId);
  const line = prescription?.lines.find((l) => l.id === lineId);
  if (!prescription || !line) return { ok: false, reason: "Ligne introuvable." };

  const { fulfillment } = line;
  if (fulfillment.dispenseState === "delivered") {
    return { ok: false, reason: "Déjà livré." };
  }
  if (fulfillment.dispenseState === "external") {
    return { ok: false, reason: "Marqué achat externe." };
  }
  if (fulfillment.billingDocumentId) {
    return { ok: false, reason: "Déjà en comptabilité." };
  }
  if (fulfillment.stockState === "shortage") {
    return { ok: false, reason: "Rupture de stock — non facturable ici." };
  }
  if (fulfillment.stockState !== "reserved") {
    return { ok: false, reason: "Validez le stock d'abord." };
  }

  const needsInsurer = prescriptionLineRequiresInsurerValidation(prescriptionId, lineId);
  if (needsInsurer) {
    const coverageCase = getCoverageCaseForPrescription(prescriptionId);
    if (!coverageCase || coverageCase.status !== "approved") {
      return { ok: false, reason: "Validation assureur requise." };
    }
  }

  return { ok: true };
}

export function canDeliverLine(prescriptionId: string, lineId: string): LineActionResult {
  const prescription = getPrescriptionById(prescriptionId);
  const line = prescription?.lines.find((l) => l.id === lineId);
  if (!prescription || !line) return { ok: false, reason: "Ligne introuvable." };

  const { fulfillment } = line;
  if (fulfillment.dispenseState === "delivered") {
    return { ok: false, reason: "Déjà livré." };
  }
  if (fulfillment.dispenseState === "external") {
    return { ok: false, reason: "Achat externe." };
  }
  if (fulfillment.stockState !== "reserved" && fulfillment.stockState !== "issued") {
    return { ok: false, reason: "Stock non réservé." };
  }
  if (!fulfillment.billingDocumentId) {
    return { ok: false, reason: "Pas encore facturé en comptabilité." };
  }

  const doc = getBillingDocument(fulfillment.billingDocumentId);
  if (!doc) return { ok: false, reason: "Dossier comptabilité introuvable." };

  const needsInsurer = prescriptionLineRequiresInsurerValidation(prescriptionId, lineId);
  if (needsInsurer) {
    const coverageCase = getCoverageCaseForPrescription(prescriptionId);
    if (!coverageCase || coverageCase.status !== "approved") {
      return { ok: false, reason: "Assureur non validé." };
    }
  }

  if (doc.payerType === "insurer") {
    if (!["issued", "paid"].includes(doc.status)) {
      return { ok: false, reason: "Facture assureur non émise (backlog)." };
    }
    return { ok: true };
  }

  if (doc.status !== "paid") {
    return { ok: false, reason: "En attente de paiement." };
  }
  return { ok: true };
}

function refreshLineReadyState(prescriptionId: string, lineId: string): void {
  const deliver = canDeliverLine(prescriptionId, lineId);
  if (deliver.ok) {
    patchPrescriptionLineFulfillment(prescriptionId, lineId, { dispenseState: "ready" });
  }
}

/** Recalcule l'état « prêt à livrer » après validation assureur. */
export function refreshPrescriptionAfterCoverageReview(prescriptionId: string): void {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription) return;
  for (const line of prescription.lines) {
    refreshLineReadyState(prescriptionId, line.id);
  }
}

export function handleBillingDocumentPaidForPrescription(documentId: string): void {
  const doc = getBillingDocument(documentId);
  if (!doc?.prescriptionId) return;

  for (const lineId of doc.prescriptionLineIds) {
    const prescription = getPrescriptionById(doc.prescriptionId);
    const line = prescription?.lines.find((l) => l.id === lineId);
    if (line?.fulfillment.billingDocumentId === documentId) {
      refreshLineReadyState(doc.prescriptionId, lineId);
    }
  }
}

export function syncPrescriptionDispenseStatus(prescriptionId: string): void {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription) return;

  const terminal = prescription.lines.every(
    (l) =>
      l.fulfillment.dispenseState === "delivered" ||
      l.fulfillment.dispenseState === "external",
  );
  if (terminal && prescription.lines.length > 0) {
    markPrescriptionDispensed(prescriptionId);
    return;
  }

  const anyInBilling = prescription.lines.some((l) => l.fulfillment.billingDocumentId);
  if (anyInBilling && prescription.status !== "dispensed") {
    markPrescriptionTransferredToBilling(prescriptionId);
  }
}

/** Valide le stock ligne par ligne (réservation ou rupture). */
export async function validatePrescriptionStockLines(
  prescriptionId: string,
  lineIds?: string[],
): Promise<{ reserved: string[]; shortage: string[] }> {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription) return { reserved: [], shortage: [] };

  const storeId = await pharmacyInventoryApi.getPharmacyStoreId();
  if (!storeId) throw new Error("Dépôt WH-PHARMA introuvable.");

  const balances = await pharmacyInventoryApi.listStockBalances();
  const availableByProduct = new Map(
    balances.map((b) => [b.productId, b.quantityAvailable]),
  );

  const targets = lineIds ?? prescription.lines.map((l) => l.id);
  const reserved: string[] = [];
  const shortage: string[] = [];

  for (const lineId of targets) {
    const line = prescription.lines.find((l) => l.id === lineId);
    if (!line || line.fulfillment.dispenseState === "delivered") continue;

    const available = availableByProduct.get(line.productId) ?? 0;
    if (available >= line.quantity) {
      await reservePrescriptionLine(prescriptionId, line, storeId);
      patchPrescriptionLineFulfillment(prescriptionId, lineId, {
        stockState: "reserved",
        dispenseState: "pending",
      });
      reserved.push(lineId);
      availableByProduct.set(line.productId, available - line.quantity);
    } else {
      if (line.fulfillment.stockState === "reserved") {
        await releasePrescriptionLine(prescriptionId, line).catch(() => undefined);
      }
      patchPrescriptionLineFulfillment(prescriptionId, lineId, {
        stockState: "shortage",
        dispenseState: "pending",
      });
      shortage.push(lineId);
    }
  }

  if (reserved.length > 0) {
    markPrescriptionPharmacistValidated(prescriptionId);
  }

  return { reserved, shortage };
}

/** Envoie les lignes sélectionnées en comptabilité (partiel). */
export async function transferPrescriptionLinesToBilling(
  prescriptionId: string,
  lineIds: string[],
): Promise<BillingDocument | null> {
  const eligible = lineIds.filter((id) => canTransferLineToBilling(prescriptionId, id).ok);
  if (eligible.length === 0) return null;

  const doc = await createBillingDocumentFromPrescriptionLines(prescriptionId, eligible);
  if (!doc) return null;

  for (const lineId of eligible) {
    patchPrescriptionLineFulfillment(prescriptionId, lineId, {
      billingDocumentId: doc.id,
    });
    refreshLineReadyState(prescriptionId, lineId);
  }

  syncPrescriptionDispenseStatus(prescriptionId);

  const patient = getPatientById(doc.patientId);
  pushLocalNotification({
    title: "Ordonnance — comptabilité",
    body: `${patient ? patientFullName(patient) : "Patient"} — ${eligible.length} ligne(s) au backlog`,
    targetRole: "HOSPITOL_PHARMACIST",
    actionUrl: "/tenant/hospitol/billing/accounting",
    eventType: "prescription.billing",
  });

  return doc;
}

/** Livre les lignes : sortie stock ISC + statut livré. */
export async function deliverPrescriptionLines(
  prescriptionId: string,
  lineIds: string[],
): Promise<{ delivered: string[]; failed: Array<{ lineId: string; reason: string }> }> {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription) return { delivered: [], failed: [] };

  const storeId = await pharmacyInventoryApi.getPharmacyStoreId();
  if (!storeId) throw new Error("Dépôt pharmacie introuvable.");

  const toDeliver: PrescriptionLine[] = [];
  const failed: Array<{ lineId: string; reason: string }> = [];

  for (const lineId of lineIds) {
    const check = canDeliverLine(prescriptionId, lineId);
    if (!check.ok) {
      failed.push({ lineId, reason: check.reason ?? "Non livrable" });
      continue;
    }
    const line = prescription.lines.find((l) => l.id === lineId);
    if (line) toDeliver.push(line);
  }

  if (toDeliver.length === 0) return { delivered: [], failed };

  await issuePrescriptionLinesAtomically(
    prescriptionId,
    toDeliver.map((l) => ({ id: l.id, productId: l.productId, quantity: l.quantity })),
    storeId,
  );

  const delivered: string[] = [];
  for (const line of toDeliver) {
    patchPrescriptionLineFulfillment(prescriptionId, line.id, {
      stockState: "issued",
      dispenseState: "delivered",
    });
    delivered.push(line.id);
  }

  syncPrescriptionDispenseStatus(prescriptionId);

  const patient = getPatientById(prescription.patientId);
  pushLocalNotification({
    title: "Médicaments livrés",
    body: `${patient ? patientFullName(patient) : "Patient"} — ${delivered.length} ligne(s)`,
    targetRole: "HOSPITOL_PHARMACIST",
    eventType: "prescription.delivered",
  });

  return { delivered, failed };
}

/** Marque des lignes en rupture comme achat externe (après impression). */
export async function markPrescriptionLinesExternal(
  prescriptionId: string,
  lineIds: string[],
): Promise<void> {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription) return;

  for (const lineId of lineIds) {
    const line = prescription.lines.find((l) => l.id === lineId);
    if (!line || line.fulfillment.stockState !== "shortage") continue;
    patchPrescriptionLineFulfillment(prescriptionId, lineId, {
      dispenseState: "external",
    });
  }
  syncPrescriptionDispenseStatus(prescriptionId);
}

export function listRemainderLines(prescriptionId: string): PrescriptionLine[] {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription) return [];
  return prescription.lines.filter(
    (l) =>
      l.fulfillment.stockState === "shortage" &&
      l.fulfillment.dispenseState !== "external" &&
      l.fulfillment.dispenseState !== "delivered",
  );
}

export function lineStatusSummary(line: PrescriptionLine): string {
  const parts = [
    PRESCRIPTION_LINE_STOCK_LABELS[line.fulfillment.stockState],
    PRESCRIPTION_LINE_DISPENSE_LABELS[line.fulfillment.dispenseState],
  ];
  if (line.fulfillment.billingDocumentId) {
    const doc = getBillingDocument(line.fulfillment.billingDocumentId);
    if (doc) parts.push(`Compta: ${doc.number}`);
  }
  return parts.join(" · ");
}
